// Shared chat-message styling for the three chat interfaces:
//   • Assistant Lion            (ExpandableAssistantLion.js)
//   • Advisor Reasoning Engine  (ExpandableAdvisorReasoning.js)
//   • Floating Chat Widget      (ChatWidget.js)
//
// The text-display rules below (paragraph / heading / list / table spacing,
// first-&-last-child resets, link + table geometry) are ported 1:1 from the
// reference project's chatModal.css. ONLY the colours are swapped for this
// app's theme tokens (primary.main / text.primary / divider) so they fit the
// dark cyan styling — every padding, margin, radius and distance matches
// chatModal. Edit here to keep all three interfaces in sync.

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import Fade from '@mui/material/Fade';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTranslation } from 'react-i18next';
import { isProtectedFileUrl, interceptProtectedLinkClick } from './auth';
import { track } from './activity/track';

// ── Sticky table cells ──
// The header row and the first column of a chat table stay in view while the
// table scrolls (feedback of the business owner, 2026-10-23: "die erste
// Kolonne soll stehen bleiben … bei allen Tabellen", "Überschriften
// fixieren"). A sticky cell needs an OPAQUE background, or the scrolled cells
// show through it: the dialog's surface of the active theme with the same
// translucent tints the bubble and the header row lay over it.
const BUBBLE_TINT = 'rgba(255, 255, 255, 0.06)';
const surfaceOf = (theme) =>
  theme.components?.MuiDialog?.styleOverrides?.paper?.background || theme.palette.background.default;
const tinted = (...tints) => (theme) =>
  `${tints.map((tint) => `linear-gradient(${tint}, ${tint})`).join(', ')}, ${surfaceOf(theme)}`;

// ── Markdown text-element styles (chatModal .bot-chat-text + .message table) ──
// Applied to a bubble that renders markdown. Values are 1:1 with chatModal.css;
// colours use theme tokens.
export const chatBubbleTextSx = {
  // paragraphs: .9em gap after each (chatModal `.bot-chat-text p`)
  '& p': { m: 0, mb: '0.9em' },
  // headings: same size as body text + .9em gap (chatModal `.bot-chat-text hN`);
  // colour stays this app's accent
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    fontSize: '1em',
    lineHeight: 'inherit',
    fontWeight: 600,
    color: 'primary.main',
    m: 0,
    mb: '0.9em',
  },
  '& strong': { color: 'primary.main', fontWeight: 600 },
  // links: weight 500, underlined (chatModal `.bot-chat-text a`)
  '& a': { color: 'primary.main', fontWeight: 500, textDecoration: 'underline' },
  // lists: .9em block gap, 1.25em indent, .25em between items
  '& ul, & ol': { m: '0.9em 0', pl: '1.25em' },
  '& li': { m: '0.25em 0', '&::marker': { color: 'primary.main' } },
  // inline code (not styled in chatModal; kept from this app's prior styling)
  '& code': {
    bgcolor: 'rgba(255, 255, 255, 0.08)',
    px: 0.5,
    py: 0.25,
    borderRadius: '4px',
    fontSize: '0.85em',
    fontFamily: 'monospace',
  },
  // tables — ported from chatModal `.message table` / `.message th, td`
  // (border-spacing 0, 15px block margin, full grid, rounded outer corners,
  //  single outer border rebuilt via the *:last-child / tr:last-child rules)
  // A wide table (many columns, long nowrap headers) must never spill past the
  // response window. `overflow` on a real `display: table` box does NOT create a
  // scroll container, so the table is turned into a block box (GitHub-markdown
  // pattern): it fills up to the bubble width and, when its content can't fit
  // (e.g. nowrap headers wider than the bubble), scrolls horizontally *inside*
  // its own box instead of pushing the layout out of the window.
  //
  // A long table scrolls inside its own box too (maxHeight): the sticky header
  // row stays visible, and the horizontal scrollbar sits at the bottom of that
  // box — on screen — instead of below the last of 40 rows (feedback
  // 2026-10-23: "um nach rechts zu gehen, muss ich ganz nach unten scrollen").
  '& table': {
    display: 'block',
    width: 'max-content',
    maxWidth: '100%',
    maxHeight: '60vh',
    overflow: 'auto',
    borderCollapse: 'separate',
    borderSpacing: 0,
    mt: '15px',
    mb: '15px',
    // Standard scrollbar styling (Firefox, and Chrome since 121 — which then
    // ignores the ::-webkit-scrollbar rules below and drew white bars).
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(255, 255, 255, 0.25) transparent',
  },
  // Always-visible thin scrollbar: without it a wider-than-bubble table just
  // looks cut off at the right edge — the user can't tell it scrolls.
  '& table::-webkit-scrollbar': { height: '6px', width: '6px' },
  '& table::-webkit-scrollbar-thumb': {
    bgcolor: 'rgba(255, 255, 255, 0.25)',
    borderRadius: '3px',
  },
  '& table::-webkit-scrollbar-track': { bgcolor: 'rgba(255, 255, 255, 0.06)' },
  '& th, & td': {
    border: '1px solid',
    borderColor: 'divider',
    p: '0.35rem 0.50rem',
    textAlign: 'left',
    fontSize: '14px',
    // Words and figures never break in the middle ("Ad / vis / or 40",
    // "10'064'64 / 9 USD"): a cell wraps at spaces only, and a table that
    // cannot fit then scrolls sideways.
    wordBreak: 'normal',
    overflowWrap: 'normal',
  },
  // Figures (right-aligned columns — markdownTables.alignNumericColumns marks
  // every figure column) stay on one line: "10'064'649 USD", "+139,5 %".
  '& td[style*="right"], & td[align="right"]': { whiteSpace: 'nowrap' },
  // The first column is the row's label: sticky, and never squeezed below a
  // readable width. Its right edge keeps a line while the rest scrolls under it.
  '& td:first-of-type, & th:first-of-type': {
    position: 'sticky',
    left: 0,
    minWidth: '7em',
    boxShadow: (theme) => `inset -1px 0 0 ${theme.palette.divider}`,
  },
  '& td:first-of-type': { zIndex: 1, background: tinted(BUBBLE_TINT) },
  '& th': {
    color: 'text.primary',
    background: tinted('rgba(255, 255, 255, 0.06)', BUBBLE_TINT),
    position: 'sticky',
    top: 0,
    zIndex: 2,
    borderRight: 0,
    borderBottom: 'none',
    p: '0.5rem 0.50rem',
    fontWeight: 600,
    // Keep every column header on a single line. A wrapped 2-line header looks
    // squished and, worse, shrinks its column to the min-content width — which
    // then forces short values (dates, counts) to wrap too. Holding the header
    // on one line sets a sensible column width; the free-text column keeps the
    // slack and wraps normally. If the resulting table is wider than the bubble,
    // the `& table` block-scroll rule above scrolls it horizontally.
    whiteSpace: 'nowrap',
  },
  '& th:first-of-type': { zIndex: 3 },
  // A leading RANK column is fixed together with the label next to it
  // (feedback 2026-10-26). ChatTable (utils/chatTable.js) marks such a table
  // and sets --sticky-second-left to the measured width of the rank column.
  '& table[data-sticky-pair] td:first-of-type, & table[data-sticky-pair] th:first-of-type': {
    minWidth: 'auto',
    boxShadow: 'none',
  },
  '& table[data-sticky-pair] td:nth-of-type(2), & table[data-sticky-pair] th:nth-of-type(2)': {
    position: 'sticky',
    left: 'var(--sticky-second-left, 0px)',
    minWidth: '7em',
    boxShadow: (theme) => `inset -1px 0 0 ${theme.palette.divider}`,
  },
  '& table[data-sticky-pair] td:nth-of-type(2)': { zIndex: 1, background: tinted(BUBBLE_TINT) },
  '& table[data-sticky-pair] th:nth-of-type(2)': { zIndex: 3 },
  '& th:last-child': { borderRight: '1px solid', borderColor: 'divider' },
  '& td': { borderBottom: 0, borderRight: 0 },
  '& td:last-child': { borderRight: '1px solid', borderColor: 'divider' },
  '& tr:first-child th:first-child': { borderTopLeftRadius: '8px' },
  '& tr:first-child th:last-child': { borderTopRightRadius: '8px' },
  '& tr:last-child td:first-child': { borderBottomLeftRadius: '8px' },
  '& tr:last-child td:last-child': { borderBottomRightRadius: '8px' },
  '& tr:last-child td': { borderBottom: '1px solid', borderColor: 'divider' },
  // first & last block sit flush with the bubble's padding
  '& > :first-child': { mt: 0, pt: 0 },
  '& > :last-child': { mb: 0, pt: 0 },
};

// ── Bubble container geometry (chatModal .bot-chat-text / .user-chat-text) ──
// font-size 16px, line-height 1.5, 7px padding, 8px (bot) / 10px (user) radius.
// Colours stay this app's: user = accent bubble, bot = translucent-on-dark.
// Text-element rules are only applied to bot bubbles (they render markdown);
// user bubbles get just the paragraph reset so multi-line input stays tidy.
// Bubble geometry constants — the row's side elements (avatar, star) derive
// their vertical alignment from the SAME numbers, so they stay in sync if the
// bubble padding / text size ever changes.
const BUBBLE_PAD_PX = 7; // bubble padding (chatModal .bot-chat-text)
const BUBBLE_FONT_PX = 15; // bubble font-size
const BUBBLE_LINE_HEIGHT = 1.5; // bubble line-height (unitless)

export const chatBubbleSx = (isUser) => ({
  // Bot bubbles render markdown (incl. wide tables) — give them the full row
  // width minus the avatar slot so tables aren't needlessly clipped in the
  // narrow chat widget. User bubbles keep the classic 70% chat look.
  maxWidth: isUser ? '70%' : 'calc(100% - 40px)',
  p: `${BUBBLE_PAD_PX}px`,
  borderRadius: isUser ? '10px' : '8px',
  bgcolor: isUser ? 'primary.main' : 'rgba(255, 255, 255, 0.06)',
  color: isUser ? 'primary.contrastText' : 'text.primary',
  fontSize: `${BUBBLE_FONT_PX}px`,
  lineHeight: BUBBLE_LINE_HEIGHT,
  textAlign: 'left',
  wordBreak: 'break-word',
  ...(isUser
    ? { '& p': { m: 0, mb: '0.9em' }, '& > :first-child': { mt: 0 }, '& > :last-child': { mb: 0 } }
    : chatBubbleTextSx),
});

// ── First-line alignment slot ──
// Height of ONE text line inside the bubble incl. its vertical padding:
//   padding-top + (font-size × line-height) + padding-bottom  ( = 38px ).
// A side element (avatar / VIP star) placed in this fixed-height, vertically
// centred, top-anchored slot lines up exactly with the FIRST line of the bubble
// — for single- AND multi-line messages (the slot never grows past line one).
export const FIRST_LINE_SLOT_PX =
  BUBBLE_PAD_PX * 2 + BUBBLE_FONT_PX * BUBBLE_LINE_HEIGHT;

export const firstLineSlotSx = {
  alignSelf: 'flex-start',
  height: `${FIRST_LINE_SLOT_PX}px`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

// ── Row wrapper (chatModal .message) ──
// flex row, top-aligned, wraps; icon on the outer edge, bubble inner.
// NOTE: no justifyContent — `row-reverse` already packs the user row to the
// right (in a reversed axis flex-start = right). Adding justifyContent
// flex-end here would pack it to the LEFT, which mis-aligned the user input.
export const chatRowSx = (isUser) => ({
  display: 'flex',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
  gap: 1,
  mb: '5px',
  flexDirection: isUser ? 'row-reverse' : 'row',
});

// ── Modal header controls ──
// Shared styling for the Clear-Chat button + Close icon in the chat modal
// headers: bordered, paper background, 14px radius, cyan hover. Spread this and
// add per-element padding (text button vs icon button).
export const modalHeaderBtnSx = {
  borderRadius: '14px',
  border: 1,
  borderColor: 'divider',
  bgcolor: 'background.paper',
  color: 'text.secondary',
  textTransform: 'none',
  '&:hover': { color: 'primary.main', bgcolor: 'action.hover', borderColor: 'primary.main' },
};

// ── Shared modal header ──
// Uniform header for every modal: icon (cyan rounded box) + title on the left,
// a 14px-radius Close button on the right. `extra` renders between the title and
// the close button (e.g. a "Clear Chat" button). Pass `onClose` to show the
// close button.
export function ModalHeader({ icon, title, onClose, extra, sx }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1, ...sx }}>
      {icon != null && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: '8px',
            bgcolor: 'rgba(25, 208, 239, 0.08)',
            color: 'primary.main',
            flexShrink: 0,
            '& .MuiSvgIcon-root': { fontSize: 18 },
          }}
        >
          {icon}
        </Box>
      )}
      <Typography sx={{ fontWeight: 400, fontSize: '15px', letterSpacing: '0.02em', flex: 1, color: 'text.primary', minWidth: 0 }} noWrap>
        {title}
      </Typography>
      {extra}
      {onClose && (
        <IconButton onClick={() => onClose()} size="small" aria-label="Close" sx={{ ...modalHeaderBtnSx, p: 0.75 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
}

// Dialog onClose handler that ignores backdrop clicks — the modal then only
// closes via the header Close button or the Escape key. Usage:
//   <Dialog onClose={closeOnlyViaButton(handleClose)} ... />
export const closeOnlyViaButton = (onClose) => (event, reason) => {
  if (reason === 'backdropClick') return;
  onClose?.(event, reason);
};

// ── Custom tooltip ──
// Brand cyan bubble + white text + matching arrow, so hover hints read as part
// of this app rather than the default grey MUI box. Uses a Fade transition
// (not MUI's default Grow): Grow scales the tooltip in, which makes the arrow
// look briefly detached from the box until the animation settles — Fade only
// changes opacity, so the arrow sits flush from the first frame.
export const InfoTooltip = styled(({ className, ...props }) => (
  <Tooltip
    {...props}
    arrow
    placement="top"
    enterDelay={200}
    enterNextDelay={200}
    TransitionComponent={Fade}
    classes={{ popper: className }}
  />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.35,
    padding: '6px 10px',
    borderRadius: 8,
    maxWidth: 220,
    boxShadow: '0 4px 14px rgba(0, 0, 0, 0.35)',
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.primary.main,
  },
}));

// ── Bot avatar ──
// Drawn entirely as SVG (circle background + glyph) inside a 40×40 viewBox
// rendered at AVATAR_SIZE. Drawing the glyph as SVG (a sparkles glyph) keeps it
// crisp at any DPI and perfectly centred. Colours come from the theme (fills set
// via sx) so it stays on-brand across theme variations. User messages have no
// avatar — only their bubble is shown.
const AVATAR_SIZE = 32;

export function BotAvatar() {
  return (
    <Box sx={firstLineSlotSx}>
      <Box
        component="svg"
        viewBox="0 0 40 40"
        aria-hidden="true"
        sx={(theme) => ({
          width: AVATAR_SIZE,
          height: AVATAR_SIZE,
          flexShrink: 0,
          display: 'block',
          '& .cm-av-bg': { fill: theme.palette.primary.main },
          '& .cm-av-fg': { fill: theme.palette.primary.contrastText },
        })}
      >
        <circle className="cm-av-bg" cx="20" cy="20" r="20" />
        {/* Sparkles (MUI AutoAwesome glyph, 24×24) centred into the 40×40 circle */}
        <path
          className="cm-av-fg"
          transform="translate(8 8)"
          d="m19 9 1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12zM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25z"
        />
      </Box>
    </Box>
  );
}

// ── Email draft review card ──
// Shows recipient / subject / body of a drafted email plus an "E-Mail öffnen"
// button that opens the default mail client with everything prefilled (mailto).
// No email is ever sent from the app. Shared by the expandable Assistant and
// the floating ChatWidget so the draft renders identically in both.
export function EmailDraftCard({ draft, openLabel }) {
  const { recipient_name, recipient_email, subject, body, mailto } = draft || {};
  return (
    <Box
      sx={{
        border: '1px solid rgba(25, 208, 239, 0.45)',
        borderRadius: '10px',
        px: 2,
        pt: 5,
        pb: 3,
        my: 0.5,
        backgroundColor: 'rgba(25, 208, 239, 0.06)',
        maxWidth: '100%',
      }}
    >
      <Typography sx={{ fontWeight: 600, color: 'primary.main', mb: 0.5, fontSize: 14 }}>
        ✉️ E-Mail-Entwurf
      </Typography>
      <Typography sx={{ fontSize: 13, color: 'text.primary' }}>
        <strong>An:</strong> {recipient_name}{recipient_email ? ` (${recipient_email})` : ''}
      </Typography>
      {!recipient_email && (
        <Typography sx={{ fontSize: 12, color: 'warning.main', mb: 0.5 }}>
          Keine E-Mail-Adresse hinterlegt — bitte im Mail-Programm ergänzen.
        </Typography>
      )}
      <Typography sx={{ fontSize: 13, color: 'text.primary', mt: 0.25 }}>
        <strong>Betreff:</strong> {subject}
      </Typography>
      <Typography
        component="div"
        sx={{ fontSize: 13, color: 'text.primary', whiteSpace: 'pre-wrap', mt: 0.75, mb: 1 }}
      >
        {body}
      </Typography>
      <Button
        variant="contained"
        size="small"
        component="a"
        href={mailto}
        onClick={() => track('email_open', { area: 'chat', title: subject || '', detail: { to: recipient_name || '' } })}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          mt: 1,
          textTransform: 'none',
          fontWeight: 700,
          // White label, forced across states (WebkitTextFillColor guards against
          // any text-fill reset that would blank it out).
          color: '#ffffff',
          WebkitTextFillColor: '#ffffff',
          backgroundColor: 'primary.main',
          '&:hover, &:focus, &:active, &:link, &:visited': {
            color: '#ffffff',
            WebkitTextFillColor: '#ffffff',
            backgroundColor: 'primary.dark',
          },
        }}
      >
        {openLabel || 'E-Mail öffnen'}
      </Button>
    </Box>
  );
}

// ── Attachment card (a file a tool produced: PDF, presentation, document) ──
// Same visual language as the e-mail draft card, but built around one link:
// the agent never puts the URL into its answer (TTS would read it out), it
// emits an `attachment` event and the file appears here as a button.
// `attachment` = { type, title, subtitle, label, url, filename, report? }.
// theScreener reports need the login token: for those URLs the click is taken
// over by openAuthenticatedFile (header auth + blob tab); every other file stays
// a plain link. Cards restored from localStorage get the same treatment.
//
// The chrome of the card follows the UI language, not the backend: the button
// of a known file type is always localised here, and a theScreener card
// carries its facts in `report` ({kind, name, names?, isin?, language,
// variant?, data_date}) so title and subtitle are built here too. The German
// `title`/`subtitle` strings stay the fallback (older cards in localStorage).
const ATTACHMENT_ICONS = { pdf: '📄', powerpoint: '📊', document: '📝' };

export const ATTACHMENT_OPEN_KEYS = {
  pdf: 'attachment_open_pdf',
  powerpoint: 'attachment_open_presentation',
  document: 'attachment_open_document',
  email: 'attachment_open_email',
};

const DATE_LOCALES = { de: 'de-CH', en: 'en-GB', fr: 'fr-CH', it: 'it-CH', es: 'es-ES' };

const uiLang = (i18n) => ((i18n && (i18n.resolvedLanguage || i18n.language)) || 'de').slice(0, 2);

// 'YYYY-MM-DD' -> '27.09.2026' / '27/09/2026' (day first in every UI language).
const formatReportDate = (iso, lang) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  if (!m) return iso || '';
  const date = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return date.toLocaleDateString(DATE_LOCALES[lang] || 'de-CH',
    { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });
};

/** Title and subtitle of a theScreener report card in the UI language. */
export function reportCardText(report, t, lang) {
  const { kind, name, names, isin, language, variant, data_date: dataDate } = report;
  const display = (names && (lang === 'de' ? names.de : names.en)) || name || '';
  const reportLang = (language || '').toUpperCase();
  const asOf = dataDate ? t('ts_data_as_of', { date: formatReportDate(dataDate, lang) }) : null;
  let parts;
  if (kind === 'stock') {
    parts = [isin && `ISIN ${isin}`, reportLang,
      t(variant === 'short' ? 'ts_variant_short' : 'ts_variant_complete'), asOf];
  } else if (kind === 'index') {
    parts = [t('ts_scope_index'), reportLang, asOf];
  } else {
    parts = [t(kind === 'sector' ? 'ts_scope_sector' : 'ts_scope_industry_group'),
      t('ts_worldwide'), reportLang, asOf];
  }
  const titleKey = { stock: 'ts_title_stock', index: 'ts_title_index', sector: 'ts_title_sector' }[kind]
    || 'ts_title_industry_group';
  return {
    title: t(titleKey, { name: display }),
    subtitle: parts.filter(Boolean).join(' · '),
  };
}

export function AttachmentCard({ attachment, openLabel }) {
  const { t, i18n } = useTranslation();
  const { type, report, label, url, filename } = attachment || {};
  let { title, subtitle } = attachment || {};
  if (report && report.kind) {
    ({ title, subtitle } = reportCardText(report, t, uiLang(i18n)));
  }
  const buttonText = ATTACHMENT_OPEN_KEYS[type] ? t(ATTACHMENT_OPEN_KEYS[type])
    : (label || openLabel || t('efd_attachment_open'));
  // Also on middle click, which would otherwise open the bare URL (401).
  // Protected files are tracked where they are opened (auth.openAuthenticatedFile).
  const openProtected = isProtectedFileUrl(url)
    ? (event) => interceptProtectedLinkClick(event, { filename })
    : () => track('file_open', { area: 'chat', title: title || filename || type, detail: { type, filename } });
  return (
    <Box
      sx={{
        border: '1px solid rgba(25, 208, 239, 0.45)',
        borderRadius: '10px',
        px: 2,
        py: 1.75,
        my: 0.5,
        backgroundColor: 'rgba(25, 208, 239, 0.06)',
        maxWidth: '100%',
      }}
    >
      <Typography sx={{ fontWeight: 600, color: 'primary.main', mb: 0.25, fontSize: 14 }}>
        {ATTACHMENT_ICONS[type] || '📎'} {title || 'Dokument'}
      </Typography>
      {subtitle && (
        <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 0.5 }}>{subtitle}</Typography>
      )}
      <Button
        variant="contained"
        size="small"
        component="a"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={openProtected}
        onAuxClick={openProtected}
        sx={{
          mt: 1,
          textTransform: 'none',
          fontWeight: 700,
          color: '#ffffff',
          WebkitTextFillColor: '#ffffff',
          backgroundColor: 'primary.main',
          '&:hover, &:focus, &:active, &:link, &:visited': {
            color: '#ffffff',
            WebkitTextFillColor: '#ffffff',
            backgroundColor: 'primary.dark',
          },
        }}
      >
        {buttonText}
      </Button>
    </Box>
  );
}

// ── Floating "scroll to latest" arrow ──
// Used with the useScrollToBottom hook: a down-arrow that fades in when the user
// has scrolled up from the bottom of the message list. Absolutely positioned, so
// its scrollable container must sit inside a `position: relative` parent for it
// to pin to the bottom of the visible message area.
//   <ScrollToBottomButton show={scroll.showScrollDown} onClick={scroll.scrollToBottom} />
export function ScrollToBottomButton({ show, onClick }) {
  return (
    <Fade in={!!show} unmountOnExit>
      <IconButton
        onClick={onClick}
        size="small"
        aria-label="Scroll to latest"
        sx={{
          position: 'absolute',
          bottom: 12,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 3,
          width: 34,
          height: 34,
          // Solid brand cyan so it clearly stands out against the dark chat.
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.45)',
          '&:hover': { bgcolor: 'primary.dark' },
        }}
      >
        <KeyboardArrowDownIcon fontSize="small" />
      </IconButton>
    </Fade>
  );
}
